import React, { useLayoutEffect, useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { QuestControlCard } from './QuestControlCard'
import { SlideControl } from '../SlideControl'
import Btn from '../Btn'
import { IQuest, IQuestFieldType, IQuestItem } from '../../server/quests'
import { QuestTextControl } from './QuestTextControl'
import { QuestTextAreaControl } from './QuestTextAreaControl'
import { QuestCheckboxGroupControl } from './QuestCheckboxGroupControl'
import { QuestRadioGroupControl } from './QuestRadioGroupControl'
import { QuestBoolControl } from './QuestBoolControl'

interface QuestFormProps {
  quest: IQuest
  onSubmit: (data: any) => Promise<void>
  isSubmitting: boolean
  isActive: boolean
}

export const QuestForm: React.FC<QuestFormProps> = ({
  quest,
  onSubmit,
  isSubmitting,
  isActive
}) => {
  const {
    control,
    setValue,
    handleSubmit,
    formState: { errors, isSubmitted, isValid }
  } = useForm({
    mode: 'onSubmit'
  })

  const items = useMemo(() => {
    return Array.isArray(quest.items) ? quest.items : []
  }, [quest])

  useLayoutEffect(() => {
    items.forEach((item: IQuestItem) => {
      setValue(item.name, item.value)
    })
  }, [items])

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className={'app-grid'}>
        {items.length > 0 &&
          items.map((item: IQuestItem, index: number) => (
            <Controller
              key={item.name}
              control={control}
              rules={{
                required: item.type !== 'bool',
                validate: (v) => item.type !== 'bool' || typeof v === 'boolean'
              }}
              render={({ field }) => (
                <QuestControlCard
                  index={index}
                  title={item.title}
                  description={item.description}
                  error={!!errors[item.name]}
                  disabled={!isActive}
                >
                  {(() => {
                    switch (item.type) {
                      case IQuestFieldType.rating:
                        return (
                          <SlideControl
                            value={field.value}
                            onChange={field.onChange}
                            items={item.items}
                            disabled={!isActive}
                          />
                        )
                      case IQuestFieldType.text:
                        return (
                          <QuestTextControl
                            value={field.value ?? ''}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            disabled={!isActive}
                          />
                        )
                      case IQuestFieldType.textarea:
                        return (
                          <QuestTextAreaControl
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            disabled={!isActive}
                          />
                        )
                      case IQuestFieldType.checkboxGroup:
                        return (
                          <QuestCheckboxGroupControl
                            options={item.options}
                            value={field.value}
                            onChange={field.onChange}
                            disabled={!isActive}
                          />
                        )
                      case IQuestFieldType.radioGroup:
                        return (
                          <QuestRadioGroupControl
                            options={item.options}
                            value={field.value ?? ''}
                            onChange={field.onChange}
                            disabled={!isActive}
                          />
                        )
                      case IQuestFieldType.bool:
                        return (
                          <QuestBoolControl
                            value={field.value}
                            onChange={field.onChange}
                            disabled={!isActive}
                          />
                        )
                      default:
                        return <></>
                    }
                  })()}
                </QuestControlCard>
              )}
              name={item.name}
            />
          ))}
      </div>
      {isActive && (
        <div className={'mt-lg-8 mt-md-8 mt-sm-6'}>
          {isSubmitted && !isValid && (
            <p
              className={
                'text-body-small text-error-color mb-lg-6 mb-md-6 mb-sm-6'
              }
            >
              Вы заполнили не все поля
            </p>
          )}

          <Btn loading={isSubmitting} type={'submit'}>
            Отправить на проверку
          </Btn>
        </div>
      )}
    </form>
  )
}
